import { addDays, fromIso, iso } from '../format/dates'
import { LOOKAHEAD_DAYS, isOpen } from './schedule'
import type { OpeningDay, SalonDayException, StaffColor, StaffProfile } from '../types/site'

/**
 * Salondagen in een gedeelde salon met één behandelkamer: per dag werkt één persoon
 * (docs/KASSA-ABONNEMENTEN.md §4). Eén bron voor wie er wanneer is: de vrije tijden in de webshop,
 * de agenda in het beheer, de footer en de terugval bij boeken rekenen allemaal met `salonDayFor`.
 * Puur: tijden, uitzonderingen en medewerkers gaan erin; de server spiegelt dezelfde regels.
 */

/** De eigenaar. Zolang er niemand anders is, doet zij alles. */
export const OWNER_STAFF_ID = 'sarah'

export const STAFF_COLORS: readonly StaffColor[] = ['clay', 'sage', 'rose', 'ink']

export type SalonDay = {
  date: string
  open: string
  close: string
  staffId: string
  /** Komt uit een uitzondering, niet uit de vaste week. */
  exception: boolean
}

const OWNER: StaffProfile = {
  id: OWNER_STAFF_ID,
  name: 'Sarah',
  initials: 'S',
  color: 'clay',
  role: 'owner',
  allTreatments: true,
  treatments: [],
  active: true,
}

const isObj = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null && !Array.isArray(v)

/**
 * De medewerkers, altijd met de eigenaar vooraan en altijd compleet: oudere opslag (zonder lijst of
 * met ontbrekende velden) breekt niets. Kapotte rijen vallen weg.
 */
export function staffList(raw?: readonly unknown[] | null): StaffProfile[] {
  const rows = (Array.isArray(raw) ? raw : []).filter(isObj).filter((r) => typeof r.id === 'string' && r.id.trim() !== '')
  const list: StaffProfile[] = rows.map((r) => {
    const owner = r.id === OWNER_STAFF_ID
    const name = typeof r.name === 'string' && r.name.trim() ? r.name.trim() : owner ? OWNER.name : 'Collega'
    return {
      id: String(r.id),
      name,
      initials: typeof r.initials === 'string' && r.initials.trim() ? r.initials.trim().slice(0, 2) : initialsOf(name),
      color: STAFF_COLORS.includes(r.color as StaffColor) ? (r.color as StaffColor) : owner ? 'clay' : 'sage',
      // De eigenaar is altijd eigenaar en altijd actief; een collega kan nooit eigenaar worden via de instellingen.
      role: owner ? 'owner' : 'staff',
      allTreatments: owner ? true : r.allTreatments !== false,
      treatments: Array.isArray(r.treatments) ? r.treatments.filter((t): t is string => typeof t === 'string') : [],
      active: owner ? true : r.active !== false,
      ...(typeof r.photo === 'string' && r.photo.trim() ? { photo: r.photo.trim() } : {}),
      ...(typeof r.intro === 'string' && r.intro.trim() ? { intro: r.intro.trim() } : {}),
      ...(typeof r.bio === 'string' && r.bio.trim() ? { bio: r.bio.trim() } : {}),
      // Alleen een collega kan op de salonpagina; aan staat het pas als het echt `true` is.
      ...(!owner && r.onSite === true ? { onSite: true } : {}),
    }
  })
  const seen = new Set<string>()
  const unique = list.filter((s) => (seen.has(s.id) ? false : (seen.add(s.id), true)))
  const owner = unique.find((s) => s.id === OWNER_STAFF_ID) ?? OWNER
  return [owner, ...unique.filter((s) => s.id !== OWNER_STAFF_ID)]
}

/** De collega's op de salonpagina: actief én door Sarah op "Toon op de salonpagina" gezet. Puur. */
export function teamOnSite(raw?: readonly unknown[] | null): StaffProfile[] {
  return staffList(raw).filter((s) => s.role === 'staff' && s.active && s.onSite === true)
}

/** Alleen geldige uitzonderingen, gesorteerd op datum; één per datum (de laatste wint). */
export function exceptionList(raw?: readonly unknown[] | null): SalonDayException[] {
  const byDate = new Map<string, SalonDayException>()
  ;(Array.isArray(raw) ? raw : []).filter(isObj).forEach((r, i) => {
    if (typeof r.date !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(r.date)) return
    const open = typeof r.open === 'string' && r.open ? r.open : undefined
    const close = typeof r.close === 'string' && r.close ? r.close : undefined
    byDate.set(r.date, {
      id: typeof r.id === 'string' && r.id ? r.id : `uitz-${r.date}-${i}`,
      date: r.date,
      ...(open && close ? { open, close } : {}),
      ...(typeof r.staffId === 'string' && r.staffId ? { staffId: r.staffId } : {}),
      ...(typeof r.note === 'string' && r.note.trim() ? { note: r.note.trim() } : {}),
    })
  })
  return [...byDate.values()].sort((a, b) => a.date.localeCompare(b.date))
}

export const initialsOf = (name: string) =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase() ?? '')
    .join('') || '?'

/** Wie, open of dicht, en de tijden op één datum. Een uitzondering gaat voor de vaste weekdag. `null` = dicht. */
export function salonDayFor(hours: readonly OpeningDay[], exceptions: readonly SalonDayException[] | undefined, date: string): SalonDay | null {
  const ex = exceptions?.find((e) => e.date === date)
  if (ex) return ex.open && ex.close ? { date, open: ex.open, close: ex.close, staffId: ex.staffId || OWNER_STAFF_ID, exception: true } : null
  const w = hours.find((h) => h.weekday === fromIso(date).getDay())
  if (!w || !isOpen(w)) return null
  return { date, open: w.open, close: w.close, staffId: w.staffId || OWNER_STAFF_ID, exception: false }
}

/** De eerstvolgende `count` salondagen (vanaf morgen), eventueel alleen die `accept` goedkeurt. */
export function upcomingSalonDays(
  hours: readonly OpeningDay[],
  exceptions: readonly SalonDayException[] | undefined,
  count: number,
  opts: { from?: Date; accept?: (d: SalonDay) => boolean } = {},
): SalonDay[] {
  const from = opts.from ?? new Date()
  const days: SalonDay[] = []
  for (let i = 1; days.length < count && i <= LOOKAHEAD_DAYS; i++) {
    const day = salonDayFor(hours, exceptions, iso(addDays(from, i)))
    if (day && (!opts.accept || opts.accept(day))) days.push(day)
  }
  return days
}

export const staffById = (staff: readonly StaffProfile[], id: string | undefined) => staff.find((s) => s.id === (id || OWNER_STAFF_ID))

/** Naam voor op het scherm; een onbekende of verwijderde medewerker valt niet weg. */
export const staffName = (staff: readonly StaffProfile[], id: string | undefined) => staffById(staff, id)?.name ?? (id && id !== OWNER_STAFF_ID ? 'Collega' : 'Sarah')

export const isOwnerId = (id: string | undefined) => !id || id === OWNER_STAFF_ID

/**
 * Naam voor "bij {naam}" bij een afspraak (bevestiging, Mijn omgeving, agenda-uitnodiging, mails).
 * Leeg zolang Sarah alleen werkt: dan voegt het niets toe. Bij een collega altijd.
 */
export function withWhom(staff: readonly StaffProfile[], id: string | undefined): string {
  const team = staff.filter((s) => s.active)
  if (team.length < 2 && isOwnerId(id)) return ''
  return staffName(staff, id)
}

/** Mag deze persoon deze behandeling doen? Gestopt of onbekend: nee. */
export function canPerform(staff: readonly StaffProfile[], staffId: string, treatmentSlug: string): boolean {
  const s = staffById(staff, staffId)
  if (!s || !s.active) return false
  return s.role === 'owner' || s.allTreatments || s.treatments.includes(treatmentSlug)
}

/** Staan er in de komende weken dagen van een collega? Dan pas toont de webshop "Alleen bij Sarah". */
export const hasColleagueDays = (hours: readonly OpeningDay[], exceptions: readonly SalonDayException[] | undefined, from?: Date) =>
  upcomingSalonDays(hours, exceptions, LOOKAHEAD_DAYS, { from }).some((d) => !isOwnerId(d.staffId))

export type PerformerIssue = 'dicht' | 'andere-persoon' | 'behandeling'

/**
 * Klopt de uitvoerder van een afspraak nog met de salondag? Voor de werklijst ("Uitvoerder klopt
 * niet meer") en de waarschuwing bij inplannen. `null` = in orde.
 */
export function performerIssue(
  a: { date: string; staffId: string; treatmentSlug: string },
  hours: readonly OpeningDay[],
  exceptions: readonly SalonDayException[] | undefined,
  staff: readonly StaffProfile[],
): PerformerIssue | null {
  const day = salonDayFor(hours, exceptions, a.date)
  if (!day) return 'dicht'
  if (day.staffId !== (a.staffId || OWNER_STAFF_ID)) return 'andere-persoon'
  if (!canPerform(staff, a.staffId || OWNER_STAFF_ID, a.treatmentSlug)) return 'behandeling'
  return null
}

export const performerIssueText: Record<PerformerIssue, string> = {
  dicht: 'De salon is die dag dicht',
  'andere-persoon': 'Op die dag werkt iemand anders',
  behandeling: 'Deze persoon doet deze behandeling niet',
}

/**
 * Mag deze persoon deze afspraak overnemen? Voor "Wijzigen" in het afspraakdetail en de
 * afwezigheidsflow. Leeg (`null`) = mag; anders de reden in gewone taal ("werkt die dag niet").
 */
export function performerChoice(
  a: { date: string; staffId: string; treatmentSlug: string },
  hours: readonly OpeningDay[],
  exceptions: readonly SalonDayException[] | undefined,
  staff: readonly StaffProfile[],
): string | null {
  const s = staffById(staff, a.staffId)
  if (!s || !s.active) return 'gestopt'
  const issue = performerIssue(a, hours, exceptions, staff)
  if (issue === 'dicht') return 'salon dicht'
  if (issue === 'andere-persoon') return 'werkt die dag niet'
  if (issue === 'behandeling') return 'doet deze behandeling niet'
  return null
}

/**
 * Bij wie boekt deze klant standaard? Voor "Bij wie wil je komen?" in de boekingslade.
 * 1. Een lopende kuur voor deze behandeling: bij wie de kuur loopt.
 * 2. Anders een vaste behandelaar: haar laatste twee bezoeken waren bij dezelfde persoon.
 * Alleen als die persoon nog actief is en de behandeling doet. `null` = "Maakt niet uit".
 */
export function regularPerformer(
  course: { treatmentSlug: string; staffId?: string; done: number; total: number } | null | undefined,
  visits: readonly { date: string; staffId?: string }[],
  treatmentSlug: string,
  staff: readonly StaffProfile[],
): string | null {
  const ok = (id: string) => canPerform(staff, id, treatmentSlug)
  if (course && course.treatmentSlug === treatmentSlug && course.done < course.total) {
    const id = course.staffId || OWNER_STAFF_ID
    if (ok(id)) return id
  }
  const [last, before] = [...visits].sort((a, b) => b.date.localeCompare(a.date))
  if (last && before) {
    const a = last.staffId || OWNER_STAFF_ID
    const b = before.staffId || OWNER_STAFF_ID
    if (a === b && ok(a)) return a
  }
  return null
}

/** "In de salon op maandag, dinsdag en donderdag". Leeg als er geen vaste dagen zijn. */
export function salonDaysSentence(hours: readonly OpeningDay[]): string {
  const open = hours.filter(isOpen).map((h) => h.day.toLowerCase())
  if (open.length === 0) return ''
  const list = open.length === 1 ? open[0] : `${open.slice(0, -1).join(', ')} en ${open.at(-1)}`
  return `In de salon op ${list}`
}
